import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from './api'
import { reportError } from './settings'

export const SOURCES = [
  'phone',
  'email',
  'website',
  'referral',
  'walk_in',
  'kira',
  'other',
] as const
export type Source = (typeof SOURCES)[number]
export const SOURCE_LABEL: Record<Source, string> = {
  phone: 'Phone call',
  email: 'Email',
  website: 'Website',
  referral: 'Referral',
  walk_in: 'Walk-in',
  kira: 'Kira',
  other: 'Other',
}

export const STATUSES = [
  'new',
  'contacted',
  'survey_booked',
  'quoted',
  'won',
  'lost',
  'junk',
] as const
export type EnquiryStatus = (typeof STATUSES)[number]
export const STATUS_LABEL: Record<EnquiryStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  survey_booked: 'Survey booked',
  quoted: 'Quoted',
  won: 'Won',
  lost: 'Lost',
  junk: 'Junk',
}
export const OPEN_STATUSES: EnquiryStatus[] = ['new', 'contacted', 'survey_booked']

export interface Enquiry {
  id: string
  reference: string
  source: Source
  status: EnquiryStatus
  kind: 'business' | 'domestic'
  companyName: string
  contactName: string
  jobTitle: string
  email: string
  phone: string
  address: string
  postcode: string
  town: string
  county: string
  latitude: number | null
  longitude: number | null
  services: string
  categoryIds: string[]
  notes: string
  assignedTo: string | null
  assignedName: string
  nextActionAt: string | null
  surveyAt: string | null
  lostReason: string
  customerId: string | null
  quote: { id: string; reference: string; status: string } | null
  createdAt: string
  createdByName: string
  updatedAt: string
  version: number
}

export type MatchReason = 'name' | 'email' | 'phone' | 'postcode' | 'email_domain' | 'contact_name'
export const REASON_LABEL: Record<MatchReason, string> = {
  name: 'similar name',
  email: 'same email',
  phone: 'same phone',
  postcode: 'same postcode',
  email_domain: 'same email domain',
  contact_name: 'same contact name',
}
export interface DuplicateMatch {
  customerId: string
  customerName: string
  status: string
  reasons: MatchReason[]
  score: number
  contactId: string | null
  siteId: string | null
}

export interface EnquiryEvent {
  id: string
  type: string
  actorType: string
  actorLabel: string
  detail: Record<string, unknown>
  at: string
}

export interface EnquiryDetail extends Enquiry {
  customer: { id: string; name: string } | null
  duplicates: DuplicateMatch[]
  events: EnquiryEvent[]
}

export interface BookedSurvey {
  enquiryId: string
  reference: string
  companyName: string
  contactName: string
  phone: string
  address: string
  town: string
  postcode: string
  latitude: number | null
  longitude: number | null
  surveyAt: string
  assignedName: string
  services: string
}
export interface Walkround {
  quoteId: string
  reference: string
  title: string
  customerName: string
  siteAddress: string
  areaCount: number
  photoCount: number
  ownerName: string
  updatedAt: string
}

export const enquiriesQuery = () =>
  queryOptions({
    queryKey: ['enquiries'],
    queryFn: async () => (await api.get<{ items: Enquiry[] }>('/enquiries')).items,
  })
export const enquiryQuery = (id: string) =>
  queryOptions({
    queryKey: ['enquiry', id],
    queryFn: () => api.get<EnquiryDetail>(`/enquiries/${id}`),
  })
export const assigneesQuery = queryOptions({
  queryKey: ['enquiry-assignees'],
  queryFn: async () =>
    (await api.get<{ items: { id: string; name: string }[] }>('/enquiries/assignees')).items,
  staleTime: 5 * 60_000,
})
export const surveysQuery = () =>
  queryOptions({
    queryKey: ['surveys'],
    queryFn: () => api.get<{ booked: BookedSurvey[]; walkrounds: Walkround[] }>('/surveys'),
  })

/** Run an enquiry action (contact, book-survey, mark-lost, reopen, notes). */
export function useEnquiryAction(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ action, body }: { action: string; body?: unknown; success?: string }) =>
      api.post<Enquiry>(`/enquiries/${id}/${action}`, body ?? {}),
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: ['enquiry', id] })
      void qc.invalidateQueries({ queryKey: ['enquiries'] })
      void qc.invalidateQueries({ queryKey: ['surveys'] })
      if (vars.success) toast.success(vars.success)
    },
    onError: (e) => reportError(e, () => void qc.invalidateQueries({ queryKey: ['enquiry', id] })),
  })
}

export const displayName = (e: Pick<Enquiry, 'companyName' | 'contactName'>) =>
  e.companyName || e.contactName || 'Unnamed enquiry'

export const siteLine = (e: { address: string; town: string; postcode: string }) =>
  [e.address, e.town, e.postcode].filter(Boolean).join(', ')

/** Google Maps directions to an address (or exact position when known). */
export function directionsUrl(e: {
  address: string
  town: string
  postcode: string
  latitude: number | null
  longitude: number | null
}) {
  const dest =
    e.latitude !== null && e.longitude !== null
      ? `${e.latitude},${e.longitude}`
      : siteLine(e) || e.postcode
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`
}

export const telHref = (phone: string) => `tel:${phone.replace(/[^\d+]/g, '')}`

/** "2026-09-28T09:30" in the browser's time zone, for datetime-local inputs. */
export function toLocalInput(iso: string | null) {
  if (!iso) return ''
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

export const ukDateTime = (iso: string | null) =>
  iso
    ? new Intl.DateTimeFormat('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/London',
      }).format(new Date(iso))
    : ''
